import AssignmentSettingsEditor from 'components/CreateAssignment/AssignmentSettingsEditor';
import CreateRubricAIDialog from 'components/CreateAssignment/CreateRubricAIDialog';
import CreateRubricDialog from 'components/CreateAssignment/CreateRubricDialog';
import CreateRubricExistingDialog from 'components/CreateAssignment/CreateRubricExistingDialog';
import EditContentBankResourcesDialog from 'components/CreateAssignment/EditContentBankResourcesDialog';
import EditRubricDialog from 'components/CreateAssignment/EditRubricDialog';
import ImportAssignmentSettingsDialog from 'components/CreateAssignment/ImportAssignmentSettingsDialog';
import UploadContentBankResourcesDialog from 'components/CreateAssignment/UploadContentBankResourcesDialog';
import PushRubricDialog from 'components/InstructorAssignments/PushRubricDialog';
import SelectRubricDialog from 'components/Rubrics/SelectRubricDialog';
import { useApp } from 'contexts/app';
import { useAppBar } from 'contexts/appBar';
import { useAuth } from 'contexts/auth';
import useAssignmentLmsResync from 'hooks/useAssignmentLmsResync';
import useAssignmentSettingsImport from 'hooks/useAssignmentSettingsImport';
import { cloneDeep } from 'lodash';
import { PageContext as AssignmentEditorContext } from 'pages/AssignmentEditor/PageProvider';
import { useCallback, useContext, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Loading } from 'shell/components';
import Box from 'shell/components/Box';
import { reconcileTemplatedSettings } from 'utils/assignmentRubricReconciliation';
import { mergeGradingSettings } from 'utils/gradingInstructions';

const PageLayout = ({ location, pageContext }) => {
    const {
        isLoading,
        assignments,
        batch,
        setBatch,
        submissions,
        setSubmissions,
        isGradingFeatureDisabled,
        isFeedbackFeatureDisabled,
        isInlineCommentsFeatureDisabled,
        isRubricFeaturedDisabled,
    } = useApp();
    const { setNode } = useAppBar();
    const { auth } = useAuth();
    const params = useParams();
    const { t } = useTranslation('assignment');
    const props = useContext(pageContext || AssignmentEditorContext);
    const {
        form,
        setForm,
        setErrors,
        selectRubricDialog,
        setSelectRubricDialog,
        createRubricDialog,
        setCreateRubricDialog,
        createRubricExistingDialog,
        setCreateRubricExistingDialog,
        createRubricAIDialog,
        setCreateRubricAIDialog,
        editRubricDialog,
        setEditRubricDialog,
        uploadContentBankResourcesDialog,
        setUploadContentBankResourcesDialog,
        editContentBankResourcesDialog,
        setEditContentBankResourcesDialog,
        pushRubricDialog,
        setPushRubricDialog,
        setIsSaving,
        resumeAutosave,
        isEditorReady = true,
    } = props;

    const {
        closeImportDialog,
        bumpForceRteRevision,
        dialog: importAssignmentSettingsDialog,
        forceRteRevision,
        importAssignmentSettings,
        onSettingsReplaced,
        openImportDialog,
        rolloverDisabledGroups,
        settingsRevision,
    } = useAssignmentSettingsImport({
        form,
        isFeedbackFeatureDisabled,
        isGradingFeatureDisabled,
        isInlineCommentsFeatureDisabled,
        isRubricFeaturedDisabled,
        setErrors,
        setForm,
    });

    const assignment = useMemo(() => assignments?.find(({ id }) => id === params.id), [assignments, params.id]);

    const closeSelectRubricDialog = useCallback(() => setSelectRubricDialog({ open: false }), [setSelectRubricDialog]);

    const handleClickAddRubric = useCallback(() => setCreateRubricDialog({ open: true }), [setCreateRubricDialog]);

    const handleClickUseRubric = useCallback(
        (rubric) => setEditRubricDialog({ open: true, customDefaultForm: rubric.rubric }),
        [setEditRubricDialog]
    );

    const closeCreateRubricDialog = useCallback(() => setCreateRubricDialog({ open: false }), [setCreateRubricDialog]);

    const handleClickCreateExisting = useCallback(
        () => setCreateRubricExistingDialog({ open: true }),
        [setCreateRubricExistingDialog]
    );

    const handleClickCreateAI = useCallback(() => setCreateRubricAIDialog({ open: true }), [setCreateRubricAIDialog]);

    const handleClickCreateManual = useCallback(
        () => setEditRubricDialog({ open: true, customDefaultForm: null }),
        [setEditRubricDialog]
    );

    const closeCreateRubricExistingDialog = useCallback(
        () => setCreateRubricExistingDialog({ open: false }),
        [setCreateRubricExistingDialog]
    );

    const closeCreateRubricAIDialog = useCallback(
        () => setCreateRubricAIDialog({ open: false }),
        [setCreateRubricAIDialog]
    );

    const closeEditRubricDialog = useCallback(
        () => setEditRubricDialog({ open: false, customDefaultForm: null }),
        [setEditRubricDialog]
    );

    const handleSaveRubric = useCallback(
        (rubric) => {
            setForm((current) => {
                const criterionIds = new Set(rubric.criteria.map(({ id }) => String(id)));
                let newForm = cloneDeep({ ...current, rubric });

                const templatedSettingsNeedingUpdate = [];
                ['feedbackSettings', 'studentFeedbackSettings'].forEach((field) => {
                    if (current[field]?.structure !== 'templated') return;
                    const result = reconcileTemplatedSettings(current[field], rubric);
                    newForm[field] = result.settings;
                    if (result.hasMissingInstructions) templatedSettingsNeedingUpdate.push(field);
                });

                if (templatedSettingsNeedingUpdate.length) {
                    setErrors((current) =>
                        templatedSettingsNeedingUpdate.reduce(
                            (next, field) => ({ ...next, [field]: `(${t('needs-update', 'needs update')})` }),
                            current
                        )
                    );
                }

                let newGradingSettings = cloneDeep(current.gradingSettings);
                if (newGradingSettings?.criteria) {
                    // remove keys that have been removed from the rubric
                    Object.keys(newGradingSettings.criteria).forEach((criterionId) => {
                        if (!criterionIds.has(String(criterionId))) {
                            delete newGradingSettings.criteria[criterionId];
                        }
                    });

                    // check if any criteria were added
                    const wereNoAdditionsMade = rubric.criteria.every(({ id: criterionId }) => {
                        return Object.prototype.hasOwnProperty.call(newGradingSettings.criteria, criterionId);
                    });

                    if (!wereNoAdditionsMade) {
                        setErrors((current) => ({
                            ...current,
                            gradingSettings: `(${t('needs-update', 'needs update')})`,
                        }));
                    }
                }

                if (newGradingSettings?.criteria)
                    newForm.gradingSettings = mergeGradingSettings(newGradingSettings, newGradingSettings.criteria);
                return newForm;
            });
        },
        [setErrors, setForm, t]
    );

    const closeUploadContentBankResourcesDialog = useCallback(
        () => setUploadContentBankResourcesDialog({ open: false }),
        [setUploadContentBankResourcesDialog]
    );

    const onClickUploadContentBankResources = useCallback(
        (files) => {
            setForm((current) => ({
                ...current,
                contentBankResources: [...current.contentBankResources, ...files],
            }));
            setEditContentBankResourcesDialog({ open: true });
        },
        [setEditContentBankResourcesDialog, setForm]
    );

    const closeEditContentBankResourcesDialog = useCallback(
        () => setEditContentBankResourcesDialog({ open: false }),
        [setEditContentBankResourcesDialog]
    );

    const handleSaveContentBankResources = useCallback(
        (contentBankResources) => setForm((current) => ({ ...current, contentBankResources })),
        [setForm]
    );

    const closePushRubricDialog = useCallback(() => {
        setIsSaving(false);
        resumeAutosave?.();
        setPushRubricDialog({ open: false });
    }, [resumeAutosave, setIsSaving, setPushRubricDialog]);

    const handleResyncAPIAssignment = useAssignmentLmsResync({
        assignment,
        auth,
        form,
        onSettingsReplaced,
        setForceRteUpdate: bumpForceRteRevision,
        setBatch,
        setForm,
        setSubmissions,
    });

    useEffect(() => {
        setNode(null);
        return () => setNode(null);
    }, [setNode]);

    const layoutProps = {
        ...props,
        isNew: location.pathname.endsWith('/new') && !props.isPersisted,
        isDraft: props.isPersisted ? props.isDraft : Boolean(assignment?.is_draft),
        settingsRevision,
        onSettingsReplaced,
        handleResyncAPIAssignment,
        forceRTEUpdate: forceRteRevision,
        onClickImportAssignmentSettings: openImportDialog,
    };

    return isLoading || !form || !isEditorReady ? (
        <Loading />
    ) : (
        <>
            <Box sx={{ width: '100%', minWidth: 0, minHeight: '100%' }}>
                <AssignmentSettingsEditor {...layoutProps} batch={batch || []} submissions={submissions || []} />
            </Box>
            <SelectRubricDialog
                open={selectRubricDialog.open}
                onClose={closeSelectRubricDialog}
                onClickAddRubric={handleClickAddRubric}
                onClickUseRubric={handleClickUseRubric}
            />
            <CreateRubricDialog
                open={createRubricDialog.open}
                onClose={closeCreateRubricDialog}
                onClickCreateExisting={handleClickCreateExisting}
                onClickCreateAI={handleClickCreateAI}
                onClickCreateManual={handleClickCreateManual}
            />
            <CreateRubricExistingDialog
                open={createRubricExistingDialog.open}
                onClose={closeCreateRubricExistingDialog}
                onCreate={handleSaveRubric}
            />
            <CreateRubricAIDialog
                open={createRubricAIDialog.open}
                title={form.title}
                description={form.description}
                onClose={closeCreateRubricAIDialog}
                onCreate={handleSaveRubric}
            />
            <EditRubricDialog
                open={editRubricDialog.open}
                assignment={form}
                rubric={form.rubric}
                customDefaultForm={editRubricDialog.customDefaultForm}
                onClose={closeEditRubricDialog}
                onSave={handleSaveRubric}
            />
            <UploadContentBankResourcesDialog
                open={uploadContentBankResourcesDialog.open}
                resources={form.contentBankResources}
                onClose={closeUploadContentBankResourcesDialog}
                onClick={onClickUploadContentBankResources}
            />
            <EditContentBankResourcesDialog
                open={editContentBankResourcesDialog.open}
                resources={form.contentBankResources}
                onClose={closeEditContentBankResourcesDialog}
                onSave={handleSaveContentBankResources}
            />
            <PushRubricDialog
                open={pushRubricDialog.open}
                form={pushRubricDialog.form}
                onClose={closePushRubricDialog}
                onSave={pushRubricDialog.onSave}
            />
            <ImportAssignmentSettingsDialog
                currentAssignmentId={params.id}
                currentForm={form}
                disabledGroups={rolloverDisabledGroups}
                open={importAssignmentSettingsDialog.open}
                onClose={closeImportDialog}
                onImport={importAssignmentSettings}
            />
        </>
    );
};

export default PageLayout;
